import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { SERVICE_MODES, type ServiceMode } from '@shared/constants/index.ts';
import { classes } from './ui.tsx';

/**
 * Homes, businesses, or both — asked at signup and again on the company
 * settings card, from one component so both places ask it the same way.
 *
 * It is a set of radio cards rather than a dropdown on purpose. The answer
 * decides which starter library the account carries and what a new quote
 * assumes, and a picker that shows one option at a time hides two thirds of
 * that decision behind a click: people took the default without ever learning
 * there was a commercial side to the app, and only found it out from support.
 * Each card therefore says in a line what choosing it means.
 *
 * `value` may be null, which is "nobody has answered yet" — how the signup
 * form starts, so the choice is deliberate rather than inherited from
 * whichever option happened to be listed first.
 */
export default function ServiceModeChoice({
  value,
  onChange,
  label,
  hint,
  error,
  disabled,
  name = 'service-mode',
}: {
  value: ServiceMode | null;
  onChange: (mode: ServiceMode) => void;
  /** Overrides the shared question, for a screen that asks it differently. */
  label?: string;
  hint?: string;
  error?: string | undefined;
  disabled?: boolean;
  /** Distinct per form, so two pickers on one page cannot share a group. */
  name?: string;
}) {
  const { t } = useTranslation();
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;

  return (
    <fieldset className="mb-4" aria-describedby={describedBy} aria-invalid={error ? true : undefined}>
      <legend className="field-label">{label ?? t('serviceModeChoice.legend')}</legend>
      <div className="grid gap-2 sm:grid-cols-3">
        {SERVICE_MODES.map((mode) => (
          <label
            key={mode}
            className={classes(
              'flex cursor-pointer items-start gap-2 rounded-md border p-3',
              value === mode
                ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)]'
                : 'border-[color:var(--color-line)]',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            <input
              type="radio"
              className="mt-1"
              name={name}
              value={mode}
              checked={value === mode}
              disabled={disabled}
              onChange={() => onChange(mode)}
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium">{t(`serviceModeChoice.modes.${mode}.title`)}</span>
              <span className="block text-sm text-[color:var(--color-muted)]">
                {t(`serviceModeChoice.modes.${mode}.description`)}
              </span>
            </span>
          </label>
        ))}
      </div>
      {hint ? (
        <p className="field-hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="field-error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
